// @ts-ignore
import ExternalAI from 'assets/images/onboarding/slides/external_ai.webp';
// @ts-ignore
import FileIntelligence from 'assets/images/onboarding/slides/file_intelligence.webp';
// @ts-ignore
import KnowledgeCapture from 'assets/images/onboarding/slides/knowledge_capture.webp';
// @ts-ignore
import MeetRipley from 'assets/images/onboarding/slides/meet_ripley.webp';
// @ts-ignore
import TeamCollaboration from 'assets/images/onboarding/slides/team_collaboration.webp';
// @ts-ignore
import Workspaces from 'assets/images/onboarding/slides/workspaces.webp';
import theme from 'config/theme';
import Typography from 'interface/base/Typography';
import Modal from 'interface/common/Modal';
import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {LayoutOnboardSlide} from './LayoutOnboardSlide';

export const SLIDES_KEY = 'show_onboard_slides';

const SLIDES = [
  {
    title: 'Welcome to Fast.io!',
    description:
      'Fast.io transforms your team’s files into living knowledge that grows smarter over time.',
    small: 'Upload once. Use everywhere. Remember everything.',
    buttonText: 'Continue',
  },
  {
    title: 'Workspaces',
    description: 'Workspaces organize your team’s knowledge',
    small:
      "Create dedicated spaces for projects, departments, or topics to keep your team's knowledge structured and accessible.",
    image: Workspaces,
    buttonText: 'Continue',
  },
  {
    title: 'File Intelligence',
    description: 'Your files become smart in Fast.io',
    small:
      'We automatically analyze your content, extract insights, and make everything searchable by meaning, not just keywords.',
    image: FileIntelligence,
    buttonText: 'Continue',
  },
  {
    title: 'Meet Ripley',
    description: 'Introducing Ripley, your AI assistant',
    small:
      "Ripley lives inside your workspaces, ready to analyze documents, answer questions, and generate insights from your team's knowledge.",
    image: MeetRipley,
    buttonText: 'Continue',
  },
  {
    title: 'Knowledge Capture',
    description: 'Save AI insights back to your workspace',
    small:
      'Capture valuable insights generated by Ripley and store them alongside your original files, creating an intelligence ecosystem that grows over time.',
    image: KnowledgeCapture,
    buttonText: 'Continue',
  },
  {
    title: 'External AI Connection',
    description: 'Connect with your favorite AI tools',
    small:
      'Beyond Ripley, Fast.io works with Claude, Cursor, and other AI tools through the Model Context Protocol—your knowledge, everywhere.',
    image: ExternalAI,
    buttonText: 'Continue',
  },
  {
    title: 'Team Collaboration',
    description: 'Your team’s knowledge flows to everyone',
    small:
      'Share insights across workspaces, preserve institutional memory, and make expertise available to everyone—no matter where they work.',
    image: TeamCollaboration,
    buttonText: 'Done',
  },
];

type Props = {
  onClose: () => void;
};

export function OnboardSlideModal({onClose}: Props) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const isFirstSlide = currentSlideIndex === 0;
  const isLastSlide = currentSlideIndex === SLIDES.length - 1;
  const currentSlide = SLIDES[currentSlideIndex];

  const getLastSlideButtonText = () => {
    return 'Done';
  };

  const handleNextSlide = async () => {
    if (isLastSlide) {
      onClose();
    }

    setCurrentSlideIndex(currentSlideIndex + 1);
  };

  const handlePrevSlide = () => {
    setCurrentSlideIndex(currentSlideIndex - 1);
  };

  if (!currentSlide) {
    return null;
  }

  return (
    <Modal containerStyle={styles.container} bodyStyle={styles.body}>
      <LayoutOnboardSlide
        showBackButton={!isFirstSlide}
        buttonText={
          isLastSlide ? getLastSlideButtonText() : currentSlide.buttonText
        }
        buttonOnPress={handleNextSlide}
        onBackButtonPress={handlePrevSlide}
        image={currentSlide.image}
        isLastSlide={isLastSlide}>
        <Typography
          overrides={
            isFirstSlide
              ? styles.firstText
              : isLastSlide
                ? styles.lastText
                : styles.text
          }
          variant="bold"
          size="3xl"
          color={theme.colors.brand.$1_500}>
          {currentSlide.title}
        </Typography>
        <Typography
          overrides={
            isFirstSlide
              ? styles.firstText
              : isLastSlide
                ? styles.lastText
                : styles.text
          }
          variant="bold"
          size="3xl"
          color={theme.colors.neutral.$700}>
          {currentSlide.description}
        </Typography>
        <Typography
          variant="regular"
          size={isFirstSlide ? 'lg' : 'base'}
          color={theme.colors.neutral.$400}>
          {currentSlide.small}
        </Typography>
      </LayoutOnboardSlide>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 950,
    height: 420,
    padding: 32,
  },
  body: {
    height: '100%',
  },
  firstText: {
    fontSize: 32,
    lineHeight: 42,
    marginBottom: 16,
  },
  text: {
    fontSize: 28,
    lineHeight: 34,
    marginBottom: 12,
  },
  lastText: {
    fontSize: 32,
    lineHeight: 42,
    marginBottom: 12,
  },
});
